import { Box, Stack, IconButton } from '@mui/material';
import BaseNumberField from 'common/components/base-number-field';
import BaseTextField from 'common/components/base-text-field';
import RequiredLabel from 'common/components/required-label';
import { styleInput, styleInputNumber } from 'common/constants';
import React, { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { createStyleSheet } from 'utils/helpers';
import { IQuestionAnswer, IQuestionBaseProps } from 'types/campaign-design';
import QuestionDnD from './question-dnd';
import MinusIcon from '@mui/icons-material/Remove';
import PlusIcon from '@mui/icons-material/Add';

const QuestionTextboxRecommendMultiChoice: React.FC<IQuestionBaseProps> = ({
    question,
    ...props
}) => {
    const { watch, control, setValue } = useFormContext();
    const { remove, insert } = useFieldArray({
        control,
        name: `data.answers`,
    });

    const numberOfRows = watch(`data.numberOfRows`);
    const answers = watch('data.answers');
    const minimumAnswerChoice = watch(`data.minimumAnswerChoice`);
    const maximumAnswerChoice = watch(`data.maximumAnswerChoice`);

    const removeAnswer = (fieldIndex: number) => {
        remove(fieldIndex);
        setValue('data.numberOfRows', numberOfRows ? numberOfRows - 1 : 0);
    };

    const insertAnswer = (fieldIndex: number) => {
        insert(fieldIndex + 1, {
            id: null,
            answer: `Tuỳ chọn ${numberOfRows + 1}`,
        });
        setValue('data.numberOfRows', numberOfRows + 1);
    };

    useEffect(() => {
        if (numberOfRows >= 2) {
            // Đảm bảo minimumAnswerChoice không vượt quá giới hạn
            if (minimumAnswerChoice > numberOfRows - 1) {
                setValue('data.minimumAnswerChoice', numberOfRows - 1);
            }
            // Đảm bảo maximumAnswerChoice không vượt quá giới hạn
            if (maximumAnswerChoice > numberOfRows) {
                setValue('data.maximumAnswerChoice', numberOfRows);
            }
            // Kiểm tra nếu max < min
            if (maximumAnswerChoice < minimumAnswerChoice) {
                setValue('data.minimumAnswerChoice', maximumAnswerChoice);
            }
            // Kiểm tra nếu min và max bằng nhau
            if (maximumAnswerChoice === minimumAnswerChoice) {
                const newMin = Math.max(1, minimumAnswerChoice - 1);
                setValue('data.minimumAnswerChoice', newMin);
            }
        }
        if (numberOfRows < 2) {
            setValue('data.minimumAnswerChoice', 1);
            setValue('data.maximumAnswerChoice', 1);
        }
    }, [numberOfRows, minimumAnswerChoice, maximumAnswerChoice, setValue]);

    return (
        <Box>
            <Stack p={'0px 35px 35px 35px'} gap={3}>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 8 }}>
                    <QuestionDnD {...props} />
                    {!question.answerFromOtherQuestionLogic?.id &&
                        answers?.length === 0 && (
                            <Box
                                display={'flex'}
                                alignItems={'center'}
                                width={'100%'}
                            >
                                <IconButton onClick={() => insertAnswer(0)}>
                                    <PlusIcon />
                                </IconButton>
                                <Box component={'span'}>Thêm đáp án</Box>
                            </Box>
                        )}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            width: '100%',
                        }}
                    >
                        {answers?.map(
                            (item: IQuestionAnswer, fieldIndex: number) => {
                                const answerValue = watch(
                                    `data.answers.${fieldIndex}.answer`
                                );
                                return (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-start',
                                            gap: 1,
                                        }}
                                    >
                                        <BaseTextField
                                            autoComplete="off"
                                            fullWidth
                                            name={`data.answers.${fieldIndex}.answer`}
                                            value={answerValue}
                                            size="small"
                                            placeholder={`Nhập nội dung hàng ${fieldIndex + 1}`}
                                            sx={styles.answer}
                                            InputProps={{
                                                inputProps: {
                                                    maxLength: 255,
                                                },
                                            }}
                                        />
                                        <Box
                                            display={'flex'}
                                            marginLeft={'15px'}
                                        >
                                            {answers.length > 1 && (
                                                <IconButton
                                                    onClick={() =>
                                                        removeAnswer(fieldIndex)
                                                    }
                                                >
                                                    <MinusIcon />
                                                </IconButton>
                                            )}

                                            <IconButton
                                                onClick={() =>
                                                    insertAnswer(fieldIndex)
                                                }
                                            >
                                                <PlusIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                );
                            }
                        )}
                    </Box>
                </Box>
            </Stack>
            <Stack
                flexDirection={'row'}
                flexWrap={'wrap'}
                gap={4}
                p={'35px'}
                sx={{
                    borderTop: `2px dashed var(--color-primary)`,
                }}
                display={'none'}
            >
                <Stack flexDirection={'column'} gap={'20px'}>
                    <RequiredLabel
                        label="Số hàng"
                        required={false}
                        sx={{ fontSize: '16px', fontWeight: '500' }}
                    />
                    <BaseNumberField
                        sx={styleInputNumber}
                        size="small"
                        name={`data.numberOfRows`}
                        min={1}
                        max={20}
                        InputProps={{
                            readOnly: true,
                        }}
                        forceDisableMinus={
                            !!question.answerFromOtherQuestionLogic?.id
                        }
                        forceDisablePlus={
                            !!question.answerFromOtherQuestionLogic?.id
                        }
                        disabled={!!question.answerFromOtherQuestionLogic?.id}
                        showMinus={false}
                        showPlus={false}
                    />
                </Stack>
                {/* {watch(`data.numberOfRows`) >= 2 && (
                    <Stack gap={'20px'}>
                        <RequiredLabel
                            label="Min/max chọn đáp án"
                            required={false}
                            sx={{ fontSize: '16px' }}
                        />
                        <Stack flexDirection={'row'} gap={1}>
                            <BaseNumberField
                                sx={styleInputNumber}
                                size="small"
                                name={`data.minimumAnswerChoice`}
                                min={1}
                                max={numberOfRows - 1}
                                disabled={true}
                            />
                            <BaseNumberField
                                sx={styleInputNumber}
                                size="small"
                                name={`data.maximumAnswerChoice`}
                                min={1}
                                max={numberOfRows}
                                disabled={true}
                            />
                        </Stack>
                    </Stack>
                )} */}
            </Stack>
        </Box>
    );
};

const styles = createStyleSheet({
    answer: {
        '& .MuiInputBase-root': {
            ...styleInput['& .MuiInputBase-root'],
            height: '65px',
            borderRadius: '50px',
        },
    },
});
export default QuestionTextboxRecommendMultiChoice;
